import { AreaEmissionsProducts, MethaneLayerIds, Platforms, STACCollection } from '../../types';

/** Infers the area emissions product type (L3 or L4) from a STAC collection */
export const getProductFromCollectionId = (collectionId: string) => {
    if (collectionId.includes('Level4')) return AreaEmissionsProducts.l4;
    if (collectionId.includes('Level3')) return AreaEmissionsProducts.l3;
    throw new Error(`Unsupported product in collectionId ${collectionId}`);
};

/** Infers the platform (MAIR or MSAT) from a STAC colleciton */
export const getPlatformFromCollectionId = (collectionId: STACCollection) => {
    if (collectionId.includes('MethaneSAT')) return Platforms.MSAT;
    if (collectionId.includes('MethaneAIR')) return Platforms.MAIR;
    throw new Error(`Unsupported platform in collectionId ${collectionId}`);
};

/**
 * Returns an array with both the portal and point STAC collection names for the given
 * product level and instrument platform. Returns the STAC collections **that are needed for
 * STAC searches** -- for L3, this includes L4 collection(s).
 *
 * Used for searches across both collections, e.g. to gather captures within time
 * ranges for the deck.gl layers.
 *
 * @param platform Whether the instrument is satellite-based or aircraft-based.
 * @param product Data processing level (L3 is concentrations, L4 is emissions). Defaults to L4.
 */
export const getCollectionNameArray = (
    platform: Platforms = Platforms.MSAT,
    product: AreaEmissionsProducts
): STACCollection[] => {
    if (!product) {
        return [
            STACCollection[`${platform}_Level3_Portal`],
            STACCollection[`${platform}_Level4_Portal`],
            ...(platform === Platforms.MSAT ? [STACCollection[`${platform}_Level4_Portal_twostep`]] : []),
            STACCollection[`${platform}_Level4_point_pruned`]
        ];
    }

    const processingLevel = product === AreaEmissionsProducts.l3 ? 'Level3' : 'Level4';
    return [
        // Always request L4. Even if we're displaying L3, we want to only show data where
        // there is a corresponding L4/total available.
        STACCollection[`${platform}_Level4_Portal`],
        ...(platform === Platforms.MSAT ? [STACCollection[`${platform}_Level4_Portal_twostep`]] : []),
        // Add in L3 when we want to actually display L3 data as well
        ...(product === AreaEmissionsProducts.l3 ? [STACCollection[`${platform}_${processingLevel}_Portal`]] : []),
        // Despite the processing level possibly being L3, we always show point sources on the map,
        // so we always include the `Level4_point_pruned` collection
        STACCollection[`${platform}_Level4_point_pruned`]
    ];
};

/**
 * Determines the STAC collection that an item belongs to based on data processing level, instrument, and deck.gl layer.
 *
 *
 * - Dispersed sources and targets correspond to the `Portal` collections.
 * - Point sources correspond to the `point_pruned`collections.
 *
 * @param product Data processing level (L3 is concentrations, L4 is emissions).  Defaults to L4.
 * @param platform Whether the instrument is satellite-based or aircraft-based.
 * @param layer The layer that the data belongs to. Defaults to targets.
 */
export const getCollectionNamesForLayer = (
    product: AreaEmissionsProducts = AreaEmissionsProducts.l4,
    platform: Platforms = Platforms.MSAT,
    layer: MethaneLayerIds = MethaneLayerIds.targets
): STACCollection[] => {
    // There are no point collections for L3
    if (product === AreaEmissionsProducts.l3 && layer !== MethaneLayerIds.plumeEmissionRate)
        return [STACCollection[`${platform}_Level3_Portal`]];

    if (layer === MethaneLayerIds.areaEmissionRaster || layer === MethaneLayerIds.targets) {
        return [
            STACCollection[`${platform}_Level4_Portal`],
            ...(platform === Platforms.MSAT ? [STACCollection[`${platform}_Level4_Portal_twostep`]] : [])
        ];
    }
    return [STACCollection[`${platform}_Level4_point_pruned`]];
};
